import { DASHBOARD_GRID_SIZE } from './dashboardModuleContract';

/**
 * The dashboard's layout, saved on the project (`project.dashboardLayout`):
 *
 *   { version: 1, items: [{ module: 'siteToday', column: 0, row: 0, h: 2 }, …] }
 *
 * Each item puts a module (a registry id) with its top-left cell at
 * (column, row), 0-based on the 3×3 grid; the module covers its `size`
 * from there. A resizable module (one with `resize: {minH, maxH}`) may carry
 * its own height `h` in that range; without one it is its `size.h` tall,
 * and a stored item has `h` only when it differs from that default. The API
 * validates a layout as isValidDashboardLayout does and stores it
 * normalized (normalizeDashboardLayout). No attribute means the default
 * layout; `items: []` is a deliberately empty dashboard.
 *
 * Every function takes the registry (DASHBOARD_MODULES, or stand-ins in a
 * test) for the modules' sizes: this file imports no module.
 */

export const DASHBOARD_LAYOUT_VERSION = 1;

/** A layout has at most one module in each cell. */
export const DASHBOARD_LAYOUT_MAX_ITEMS =
  DASHBOARD_GRID_SIZE.columns * DASHBOARD_GRID_SIZE.rows;

const item = (module, column, row) => Object.freeze({ module, column, row });

/**
 * The dashboard as it stood before it could be edited: who is on the site
 * down the first column; the conditions, the TR meters and the
 * non-conformance reports down the second; the day's events over the
 * finances in the third.
 */
export const DEFAULT_DASHBOARD_LAYOUT = Object.freeze({
  version: DASHBOARD_LAYOUT_VERSION,
  items: Object.freeze([
    item('siteToday', 0, 0),
    item('sensors', 1, 0),
    item('trMeters', 1, 1),
    item('nonConformance', 1, 2),
    item('activity', 2, 0),
    item('expenses', 2, 2),
  ]),
});

/** A dashboard with no modules: the header alone. */
export const EMPTY_DASHBOARD_LAYOUT = Object.freeze({
  version: DASHBOARD_LAYOUT_VERSION,
  items: Object.freeze([]),
});

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const hasExactKeys = (value, keys) => {
  const own = Object.keys(value);
  return own.length === keys.length && keys.every((key) => own.includes(key));
};

const modulesOf = (registry) =>
  new Map(registry.map((module) => [module.id, module]));

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

/**
 * The heights a resizable module may take, `{minH, maxH}`, or null for a
 * module of one size.
 * @param {{resize?: {minH: number, maxH: number}}} module a registry entry
 * @returns {{minH: number, maxH: number}|null}
 */
export const getModuleResize = (module) =>
  module &&
  module.resize &&
  Number.isInteger(module.resize.minH) &&
  Number.isInteger(module.resize.maxH)
    ? module.resize
    : null;

/**
 * Whether an item of the module may carry its own `h`.
 * @param {Object} module a registry entry
 * @returns {boolean}
 */
export const isResizableModule = (module) => getModuleResize(module) !== null;

/**
 * Whether `h` is a height the module may take: a whole number within its
 * `resize` range, or its default height for any module.
 * @param {Object} module a registry entry
 * @param {*} h
 * @returns {boolean}
 */
export const isAllowedHeight = (module, h) => {
  if (!module || !Number.isInteger(h)) return false;
  const resize = getModuleResize(module);
  return resize ? h >= resize.minH && h <= resize.maxH : h === module.size.h;
};

/**
 * How many rows an item of the module covers: its own `h` when it is one
 * the module may take (one out of the range comes to the nearest it may),
 * otherwise the module's default (`size.h`).
 * @param {{h?: number}} item
 * @param {Object} module a registry entry
 * @returns {number}
 */
export const getItemHeight = (item, module) => {
  const resize = getModuleResize(module);
  if (!resize || !item || !Number.isInteger(item.h)) return module.size.h;
  return Math.min(Math.max(item.h, resize.minH), resize.maxH);
};

/**
 * An item's footprint in cells: the module's width, the item's height
 * (getItemHeight).
 * @param {{h?: number}} item
 * @param {Object} module a registry entry
 * @returns {{w: number, h: number}}
 */
export const getItemSize = (item, module) => ({
  w: module.size.w,
  h: getItemHeight(item, module),
});

/**
 * An item as stored: `{module, column, row}`, with `h` only when the module
 * is resizable and it is not the module's default. A module the registry
 * does not know keeps a whole-number `h` as it is.
 * @param {{module: string, column: number, row: number, h?: number}} item
 * @param {Map<string, Object>} [modules] by id
 * @returns {{module: string, column: number, row: number, h?: number}}
 */
const storedItem = ({ module, column, row, h }, modules) => {
  const entry = modules ? modules.get(module) : null;
  const keep = entry
    ? isResizableModule(entry) && Number.isInteger(h) && h !== entry.size.h
    : Number.isInteger(h);
  return keep ? { module, column, row, h } : { module, column, row };
};

const cellKey = (column, row) => `${column}-${row}`;

/**
 * The cells an item covers, `[column, row]` each.
 * @param {{column: number, row: number}} placement
 * @param {{w: number, h: number}} size
 * @returns {Array<[number, number]>}
 */
export const getItemCells = ({ column, row }, size) => {
  const cells = [];
  for (let c = 0; c < size.w; c += 1) {
    for (let r = 0; r < size.h; r += 1) cells.push([column + c, row + r]);
  }
  return cells;
};

/**
 * Whether an item stands on the grid: whole-number cells, its whole
 * footprint inside (0 … 3−w, 0 … 3−h).
 * @param {{column: *, row: *}} placement
 * @param {{w: number, h: number}} size
 * @returns {boolean}
 */
export const isItemInBounds = ({ column, row }, size) =>
  Number.isInteger(column) &&
  Number.isInteger(row) &&
  column >= 0 &&
  row >= 0 &&
  column + size.w <= DASHBOARD_GRID_SIZE.columns &&
  row + size.h <= DASHBOARD_GRID_SIZE.rows;

/**
 * Which module covers each cell: `occupancy[column][row]`, a module id or
 * null, each item by its own height (getItemSize). Items the registry does
 * not know, or off the grid, cover nothing.
 * @param {Array<{module: string, column: number, row: number, h?: number}>} items
 * @param {ReadonlyArray<Object>} registry
 * @returns {Array<Array<string|null>>}
 */
export const getDashboardOccupancy = (items, registry) => {
  const modules = modulesOf(registry);
  const occupancy = Array.from({ length: DASHBOARD_GRID_SIZE.columns }, () =>
    Array.from({ length: DASHBOARD_GRID_SIZE.rows }, () => null)
  );
  (items || []).forEach((placed) => {
    const module = modules.get(placed.module);
    if (!module) return;
    const size = getItemSize(placed, module);
    if (!isItemInBounds(placed, size)) return;
    getItemCells(placed, size).forEach(([column, row]) => {
      if (occupancy[column][row] === null) {
        occupancy[column][row] = placed.module;
      }
    });
  });
  return occupancy;
};

/**
 * Whether a module can stand with its top-left cell at (column, row): known,
 * its footprint on the grid, and every cell of it empty — the module's own
 * cells count as empty, so a placed module can move onto them, or grow over
 * them. Its height is the placement's `h` when given (one the module may
 * take: isAllowedHeight), else the one it has where the items place it,
 * else its default: a module moved keeps its height, one placed anew is its
 * default size.
 * @param {Array<{module: string, column: number, row: number, h?: number}>} items
 * @param {{module: string, column: number, row: number, h?: number}} placement
 * @param {ReadonlyArray<Object>} registry
 * @returns {boolean}
 */
export const canPlace = (items, { module, column, row, h }, registry) => {
  const entry = modulesOf(registry).get(module);
  if (!entry) return false;
  if (h !== undefined && !isAllowedHeight(entry, h)) return false;
  const current = (items || []).find((placed) => placed.module === module);
  const size = getItemSize(h !== undefined ? { h } : current || {}, entry);
  if (!isItemInBounds({ column, row }, size)) return false;
  const occupancy = getDashboardOccupancy(
    (items || []).filter((placed) => placed.module !== module),
    registry
  );
  return getItemCells({ column, row }, size).every(
    ([c, r]) => occupancy[c][r] === null
  );
};

/**
 * A layout as the API stores it: `{version, items}` only, each item
 * `{module, column, row}` and its `h` only where it is not the module's
 * default (with the registry; without one a whole-number `h` is kept as
 * it is), sorted by column and then row.
 * @param {{items?: Array}} layout
 * @param {ReadonlyArray<Object>} [registry]
 * @returns {{version: number, items: Array<{module: string, column: number, row: number, h?: number}>}}
 */
export const normalizeDashboardLayout = (layout, registry) => {
  const modules = registry ? modulesOf(registry) : null;
  return {
    version: DASHBOARD_LAYOUT_VERSION,
    items: (Array.isArray(layout?.items) ? layout.items : [])
      .map((item) => storedItem(item, modules))
      .sort(
        (a, b) =>
          a.column - b.column ||
          a.row - b.row ||
          String(a.module).localeCompare(String(b.module))
      ),
  };
};

/**
 * Whether a layout is one the API accepts (its validation, mirrored):
 * exactly `{version: 1, items}`, at most 9 items, each `{module, column,
 * row}` and optionally `h`, with a known module on the grid; `h` only on a
 * resizable module, a whole number within its `minH`–`maxH`, the footprint
 * that tall on the grid; no module twice, no cell covered twice. The order
 * of the items does not matter.
 * @param {*} layout
 * @param {ReadonlyArray<Object>} registry
 * @returns {boolean}
 */
export const isValidDashboardLayout = (layout, registry) => {
  if (!isPlainObject(layout) || !hasExactKeys(layout, ['version', 'items'])) {
    return false;
  }
  if (layout.version !== DASHBOARD_LAYOUT_VERSION) return false;
  const { items } = layout;
  if (!Array.isArray(items) || items.length > DASHBOARD_LAYOUT_MAX_ITEMS) {
    return false;
  }
  const modules = modulesOf(registry);
  const seen = new Set();
  const cells = new Set();
  return items.every((placed) => {
    if (
      !isPlainObject(placed) ||
      !hasExactKeys(
        placed,
        hasOwn(placed, 'h')
          ? ['module', 'column', 'row', 'h']
          : ['module', 'column', 'row']
      ) ||
      typeof placed.module !== 'string'
    ) {
      return false;
    }
    const module = modules.get(placed.module);
    if (!module || seen.has(placed.module)) return false;
    if (hasOwn(placed, 'h')) {
      const resize = getModuleResize(module);
      if (
        !resize ||
        !Number.isInteger(placed.h) ||
        placed.h < resize.minH ||
        placed.h > resize.maxH
      ) {
        return false;
      }
    }
    const size = getItemSize(placed, module);
    if (!isItemInBounds(placed, size)) return false;
    seen.add(placed.module);
    return getItemCells(placed, size).every(([column, row]) => {
      const key = cellKey(column, row);
      if (cells.has(key)) return false;
      cells.add(key);
      return true;
    });
  });
};

/**
 * The layout to show for a saved value (`project.dashboardLayout`):
 * - absent or malformed (not `{version: 1, items: [...]}`): the default
 *   layout (DEFAULT_DASHBOARD_LAYOUT itself);
 * - otherwise its items in order, leaving out each that is not an object,
 *   names a module the registry does not know (one of a later version, say),
 *   stands off the grid, repeats a module already kept or covers a cell one
 *   kept already covers — the first one wins — normalized. An item's `h` is
 *   taken as getItemHeight does: the module's default where it has none or
 *   the module is not resizable, the nearest allowed height where it is out
 *   of range. `items: []` stays empty, as does one whose items are all left
 *   out.
 * @param {*} saved
 * @param {ReadonlyArray<Object>} registry
 * @returns {{version: number, items: Array<{module: string, column: number, row: number, h?: number}>}}
 */
export const resolveDashboardLayout = (saved, registry) => {
  if (
    !isPlainObject(saved) ||
    saved.version !== DASHBOARD_LAYOUT_VERSION ||
    !Array.isArray(saved.items)
  ) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
  const modules = modulesOf(registry);
  const seen = new Set();
  const cells = new Set();
  const kept = [];
  saved.items.forEach((placed) => {
    if (!isPlainObject(placed)) return;
    const module = modules.get(placed.module);
    if (!module || seen.has(placed.module)) return;
    const size = getItemSize(placed, module);
    if (!isItemInBounds(placed, size)) return;
    const footprint = getItemCells(placed, size).map(([column, row]) =>
      cellKey(column, row)
    );
    if (footprint.some((key) => cells.has(key))) return;
    seen.add(placed.module);
    footprint.forEach((key) => cells.add(key));
    kept.push({
      module: placed.module,
      column: placed.column,
      row: placed.row,
      h: size.h,
    });
  });
  return normalizeDashboardLayout({ items: kept }, registry);
};

const layoutKey = (layout) =>
  normalizeDashboardLayout(layout)
    .items.map(
      ({ module, column, row, h }) =>
        `${module}@${column},${row}${h === undefined ? '' : `x${h}`}`
    )
    .join('|');

/**
 * Whether two layouts place the same modules in the same cells, of the same
 * heights, whatever the order of their items (the editor's draft against
 * what it started from). Compare resolved layouts (an absent one is the
 * empty one here; a default `h` dropped).
 * @param {{items?: Array}} a
 * @param {{items?: Array}} b
 * @returns {boolean}
 */
export const isSameLayout = (a, b) => layoutKey(a) === layoutKey(b);
